import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container } from 'reactstrap';
import { withBorder } from '../../../reactutils/withBorder';
import { withLogin } from '../../../reactutils/withLogin';
import { withMenu } from '../../../reactutils/withMenu';
import { TabbedContent } from '../../../reactutils/withTabbedScreen';
import { withTitle } from '../../../reactutils/withTitle';
import { SqlStatistics } from '../../../services/betting';
import { formatTimeDurationGerman } from '../../../utils';
import { MenuSysAdmin } from '../../MenuSysAdmin';
import { getSystemConfig, ServerType } from '../../System/systemConfig';
import { usePathSegment } from '../CacheStatisticsComponent';
import { loadSqlStatisticsAction, sqlStatsClear } from './actions';
import { sqlStatisticsServerAvailableSelector, sqlStatisticsStatsSelector } from './selectors';

export const SQLStatisticsComponent: React.FC<{}> = (props) => {
    const [tabName] = usePathSegment(3) as ServerType[];

    const stats = useSelector((state: any) => sqlStatisticsStatsSelector(state, tabName) || ([] as SqlStatistics[]));
    const serverAvailable = useSelector((state: any) => sqlStatisticsServerAvailableSelector(state, tabName) || false);

    const dispatch = useDispatch();

    const onLoad = React.useCallback(() => {
        dispatch(loadSqlStatisticsAction(tabName));
    }, [dispatch, tabName]);

    const onReload = () => {
        dispatch(loadSqlStatisticsAction(tabName));
    };
    const onClear = () => {
        dispatch(sqlStatsClear(tabName));
    };

    function formatNumber(n?: number) {
        if (n) {
            const nf = new Intl.NumberFormat(undefined, {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return nf.format(n);
        } else {
            return null;
        }
    }

    React.useEffect(() => onLoad(), [onLoad]);

    return (
        <>
            <TabbedContent
                activeTab={tabName}
                tabNames={Object.keys(getSystemConfig().serverUrls)}
                tabLinks={sysAdminSqlPath}
            />
            <Container>
                <Button className="m-2" color="secondary" onClick={onReload} outline={false}>
                    Reload
                </Button>
                {serverAvailable && (
                    <Button className="m-2" color="warning" onClick={onClear} outline={false}>
                        Clear
                    </Button>
                )}
            </Container>
            {!serverAvailable && <p className="m-2">The server ist currently not available</p>}
            {serverAvailable && (
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Statement</th>
                            <th>Count</th>
                            <th>Time</th>
                            <th>Avg. time (ms)</th>
                            <th>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats &&
                            stats.map((stat, index) => (
                                <tr key={index}>
                                    <td>{stat.name}</td>
                                    <td>{stat.executionCount}</td>
                                    <td>{formatTimeDurationGerman(stat.executionTime)}</td>
                                    <td>
                                        {stat.executionCount && stat.executionTime
                                            ? formatNumber(stat.executionTime / stat.executionCount)
                                            : 0}
                                    </td>
                                    <td>{stat.percentage && formatNumber(stat.percentage / 100.0)}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            )}
        </>
    );
};

export const sysAdminSqlPath = (serverName: string) => `/sys/sql/${serverName}`;

export const SqlStatisticsScreen = R.compose(
    withMenu(<MenuSysAdmin />),
    withBorder(),
    withTitle('SQL Statistics'),
    withLogin()
)(SQLStatisticsComponent);
